'use client';

import { motion } from 'framer-motion';
import { stripHtml } from '@/lib/homepage-mappers';
import type { SustainabilityStatsSection } from '@/types/sustainability';
import { CountUp, Reveal } from '../lib/community-reveal';
import { getCommunityStatSuffix, parseCommunityStatNumber } from '../lib/parse-community-stat';

interface CommunityStatsSectionProps {
  zone: SustainabilityStatsSection;
}

export default function CommunityStatsSection({ zone }: CommunityStatsSectionProps) {
  const stats = zone.stats ?? [];
  if (stats.length === 0) return null;

  const rows = [stats.slice(0, 4), stats.slice(4, 8)].filter((row) => row.length > 0);

  return (
    <section className="bg-gradient-to-b from-white to-[#f7f4f1] px-4 sm:px-6 md:px-16 lg:px-24">
      <div className="container mx-auto max-sm:px-4">
        <div className="flex flex-col gap-6">
          {zone.heading ? (
            <Reveal direction="up" delay={80}>
              <h2 className="text-[1.75rem] font-bold leading-[1.12] tracking-tight text-[#1a1a1a] sm:text-4xl md:text-[2.75rem] lg:text-5xl">
                {zone.heading}
              </h2>
            </Reveal>
          ) : null}

          {zone.description ? (
            <Reveal direction="left" delay={160}>
              <p className="max-w-md text-sm leading-7 text-stone-600 sm:text-[15px] sm:leading-8">
                {stripHtml(zone.description)}
              </p>
            </Reveal>
          ) : null}
        </div>

        <div className="mt-8 h-px w-full bg-stone-100 sm:mt-10" />

        {rows.map((row, rowIndex) => (
          <div key={row[0].id}>
            <div className="grid grid-cols-2 divide-x divide-stone-100 sm:grid-cols-4">
              {row.map((stat, i) => (
                <motion.div
                  key={stat.id}
                  className="group px-4 py-6 sm:px-6 sm:py-8 md:px-8 md:py-10"
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-5% 0px' }}
                  transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  <p className="text-2xl font-bold leading-none tracking-tight text-[#ed1c22] sm:text-3xl md:text-4xl lg:text-5xl">
                    <CountUp
                      to={parseCommunityStatNumber(stat)}
                      suffix={getCommunityStatSuffix(stat)}
                      duration={1800}
                    />
                  </p>
                  {stat.statheading ? (
                    <p className="mt-4 text-sm font-semibold leading-snug text-[#1a1a1a]">
                      {stat.statheading}
                    </p>
                  ) : null}
                  {stat.statDescription ? (
                    <p className="mt-1 text-xs leading-relaxed text-stone-500 sm:text-sm">
                      {stat.statDescription}
                    </p>
                  ) : null}
                </motion.div>
              ))}
            </div>
            {rowIndex < rows.length - 1 ? <div className="h-px bg-stone-100" /> : null}
          </div>
        ))}
      </div>
    </section>
  );
}
